import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, X, Download, LayoutDashboard, Briefcase, FileText, Users, Mail, Settings } from "lucide-react";
import Seo from "@/components/Seo";
import { LogoImage } from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "applications", label: "Applications", icon: FileText },
  { key: "hire", label: "Hire Requests", icon: Users },
  { key: "candidates", label: "Candidates", icon: Users },
  { key: "contacts", label: "Contacts", icon: Mail },
  { key: "settings", label: "Settings", icon: Settings },
];

const APPLICATION_STATUSES = ["New Application", "Under Review", "Shortlisted", "Interviewing", "Offer Extended", "Placed", "Not Proceeding"];
const HIRE_STATUSES = ["New Enquiry", "Contacted", "Qualified", "Proposal Sent", "Won", "Lost", "Closed"];
const CANDIDATE_STATUSES = ["New Profile", "Under Review", "Active", "Placed", "Archived"];
const CONTACT_STATUSES = ["New", "Responded", "Closed"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const StatusSelect = ({ value, options, onSave, testid }) => (
  <select
    value={value}
    onChange={(e) => onSave(e.target.value)}
    className="cursor-pointer appearance-none border border-white/10 bg-obsidian px-3 py-2 text-xs text-white/80 focus:border-emerald-500/60 focus:outline-none [&>option]:bg-obsidian"
    data-testid={testid}
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const Th = ({ children }) => (
  <th className="whitespace-nowrap border-b border-white/10 px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
    {children}
  </th>
);

const Td = ({ children, className = "", ...rest }) => (
  <td className={`border-b border-white/[0.06] px-4 py-3.5 align-top text-sm text-white/70 ${className}`} {...rest}>
    {children}
  </td>
);

const EMPTY_JOB = {
  title: "", location: "", country: "", sector: "", type: "Direct Hire", seniority: "",
  salary: "", summary: "", description: "", responsibilities: "", requirements: "", status: "Open", featured: false, demo: false,
};

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [hireRequests, setHireRequests] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [hero, setHero] = useState({ video_url: "", poster_url: "", notify_email: "" });

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);
  const [modal, setModal] = useState(null);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [jobForm, setJobForm] = useState(EMPTY_JOB);

  const logout = useCallback(() => {
    localStorage.removeItem("nexreq_token");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("nexreq_token")) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => logout())
      .finally(() => setChecking(false));
  }, [navigate, logout]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/hire-requests").then((r) => setHireRequests(r.data)).catch(() => {});
    api.get("/admin/candidates").then((r) => setCandidates(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setHero(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  const updateStatus = async (kind, id, status, setter) => {
    try {
      const r = await api.patch(`/admin/${kind}/${id}`, { status });
      setter((items) => items.map((it) => (it.id === id ? r.data : it)));
      toast.success("Status updated.");
      api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openJobModal = (job) => {
    if (job) {
      setJobForm({
        ...job,
        responsibilities: (job.responsibilities || []).join("\n"),
        requirements: (job.requirements || []).join("\n"),
      });
      setModal(job.id);
    } else {
      setJobForm(EMPTY_JOB);
      setModal("new");
    }
  };

  const saveJob = async (e) => {
    e.preventDefault();
    const payload = {
      ...jobForm,
      responsibilities: jobForm.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
      requirements: jobForm.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (modal === "new") {
        await api.post("/admin/jobs", payload);
        toast.success("Role published.");
      } else {
        await api.put(`/admin/jobs/${modal}`, payload);
        toast.success("Role updated.");
      }
      setModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this role permanently?")) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("Role deleted.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadResume = async (file, original) => {
    try {
      const res = await api.get(`/admin/files/${file}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = original || file;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated. Use it on your next sign-in.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveHero = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", hero);
      toast.success("Hero media updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (checking || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/35">Loading…</p>
      </div>
    );
  }

  const statCards = stats
    ? [
        { label: "Open Roles", value: stats.jobs_open },
        { label: "Applications", value: stats.applications, sub: `${stats.applications_new} new` },
        { label: "Hire Requests", value: stats.hire_requests, sub: `${stats.hire_requests_new} new` },
        { label: "Candidates", value: stats.candidates },
        { label: "Contacts", value: stats.contacts, sub: `${stats.contacts_new} new` },
      ]
    : [];

  return (
    <div className="flex min-h-screen bg-obsidian text-white" data-testid="admin-dashboard">
      <Seo title="Admin — NEXREQ" siteName="NEXREQ" description="NEXREQ administration dashboard." />
      <aside className="hidden w-60 shrink-0 flex-col border-r border-white/[0.08] bg-[#070C0D] lg:flex">
        <div className="flex h-20 items-center gap-3 border-b border-white/[0.08] px-6">
          <LogoImage className="h-10" alt="NEXREQ" />
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                tab === t.key ? "bg-emerald-600/15 text-emerald-400" : "text-white/55 hover:bg-panel/[0.04] hover:text-white"
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/[0.08] p-4">
          <p className="px-4 pb-2 text-xs text-white/35">{user.email}</p>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex w-full items-center gap-3 px-4 py-3 text-sm text-white/55 transition-colors hover:text-red-400"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/[0.08] px-6 py-5 lg:px-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-500">NEXREQ Admin</p>
            <h1 className="mt-1 font-heading text-2xl font-light capitalize">{TABS.find((t) => t.key === tab)?.label}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1 lg:hidden">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-2.5 py-2 text-[11px] ${tab === t.key ? "bg-emerald-600/15 text-emerald-400" : "text-white/50"}`}
                  data-testid={`admin-tab-mobile-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {tab === "jobs" && (
              <button onClick={() => openJobModal(null)} className="nx-btn-primary !px-5 !py-2.5" data-testid="admin-new-job-btn">
                <Plus size={15} /> New Role
              </button>
            )}
          </div>
        </div>

        <div className="p-6 lg:p-10">
          {tab === "overview" && (
            <div data-testid="admin-overview">
              <div className="grid gap-px border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-5">
                {statCards.map((s) => (
                  <div key={s.label} className="bg-obsidian p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">{s.label}</p>
                    <p className="mt-3 font-heading text-4xl font-light">{s.value}</p>
                    {s.sub && <p className="mt-1 text-xs text-emerald-400">{s.sub}</p>}
                  </div>
                ))}
              </div>
              <div className="mt-10 grid gap-8 lg:grid-cols-2">
                <div className="border border-white/[0.08] bg-surface p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">Latest Applications</p>
                  <div className="mt-4 space-y-3">
                    {applications.slice(0, 5).map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-4 text-sm">
                        <span className="truncate text-white/70">{a.name} — {a.job_title}</span>
                        <span className="shrink-0 font-mono text-[10px] text-emerald-400">{a.status}</span>
                      </div>
                    ))}
                    {applications.length === 0 && <p className="text-sm text-white/35">No applications yet.</p>}
                  </div>
                </div>
                <div className="border border-white/[0.08] bg-surface p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">Latest Hire Requests</p>
                  <div className="mt-4 space-y-3">
                    {hireRequests.slice(0, 5).map((h) => (
                      <div key={h.id} className="flex items-center justify-between gap-4 text-sm">
                        <span className="truncate text-white/70">{h.company} — {h.role_title}</span>
                        <span className="shrink-0 font-mono text-[10px] text-emerald-400">{h.status}</span>
                      </div>
                    ))}
                    {hireRequests.length === 0 && <p className="text-sm text-white/35">No hire requests yet.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "jobs" && (
            <div className="overflow-x-auto border border-white/[0.08]" data-testid="admin-jobs-table">
              <table className="w-full min-w-[900px]">
                <thead className="bg-surface">
                  <tr>
                    <Th>Reference</Th><Th>Title</Th><Th>Location</Th><Th>Sector</Th><Th>Type</Th><Th>Status</Th><Th>Featured</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id} className="transition-colors hover:bg-panel/[0.02]">
                      <Td className="font-mono text-xs text-white/40">{j.reference}</Td>
                      <Td className="font-medium text-white">{j.title}{j.demo ? <span className="ml-2 bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/40">Demo</span> : null}</Td>
                      <Td>{j.location}</Td>
                      <Td>{j.sector}</Td>
                      <Td>{j.type}</Td>
                      <Td>
                        <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-wider ${j.status === "Open" ? "bg-emerald-600/15 text-emerald-400" : "bg-white/[0.06] text-white/40"}`}>
                          {j.status}
                        </span>
                      </Td>
                      <Td>{j.featured ? "Yes" : "—"}</Td>
                      <Td>
                        <div className="flex gap-3">
                          <button onClick={() => openJobModal(j)} className="text-xs text-emerald-400 hover:underline" data-testid={`admin-edit-job-${j.id}`}>Edit</button>
                          <button onClick={() => deleteJob(j.id)} className="text-xs text-red-400/80 hover:underline" data-testid={`admin-delete-job-${j.id}`}>Delete</button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "applications" && (
            <div className="overflow-x-auto border border-white/[0.08]" data-testid="admin-applications-table">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-surface">
                  <tr>
                    <Th>Reference</Th><Th>Candidate</Th><Th>Role</Th><Th>Contact</Th><Th>Resume</Th><Th>Submitted</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((a) => (
                    <tr key={a.id} className="transition-colors hover:bg-panel/[0.02]">
                      <Td className="font-mono text-xs text-white/40">{a.reference}</Td>
                      <Td>
                        <p className="font-medium text-white">{a.name}</p>
                        {a.location && <p className="text-xs text-white/40">{a.location}</p>}
                      </Td>
                      <Td>{a.job_title}</Td>
                      <Td>
                        <p className="text-xs">{a.email}</p>
                        {a.phone && <p className="text-xs text-white/40">{a.phone}</p>}
                      </Td>
                      <Td>
                        {a.resume_file ? (
                          <button onClick={() => downloadResume(a.resume_file, a.resume_original)} className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:underline" data-testid={`admin-resume-${a.id}`}>
                            <Download size={13} /> {a.resume_original || "Download"}
                          </button>
                        ) : (
                          <span className="text-xs text-white/30">—</span>
                        )}
                      </Td>
                      <Td className="text-xs text-white/40">{fmtDate(a.created_at)}</Td>
                      <Td>
                        <StatusSelect value={a.status} options={APPLICATION_STATUSES} testid={`admin-application-status-${a.id}`} onSave={(s) => updateStatus("applications", a.id, s, setApplications)} />
                      </Td>
                    </tr>
                  ))}
                  {applications.length === 0 && (
                    <tr><Td className="py-10 text-center text-white/35" colSpan={7}>No applications yet.</Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "hire" && (
            <div className="overflow-x-auto border border-white/[0.08]" data-testid="admin-hire-table">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-surface">
                  <tr>
                    <Th>Reference</Th><Th>Company</Th><Th>Role</Th><Th>Contact</Th><Th>Details</Th><Th>Submitted</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {hireRequests.map((h) => (
                    <tr key={h.id} className="transition-colors hover:bg-panel/[0.02]">
                      <Td className="font-mono text-xs text-white/40">{h.reference}</Td>
                      <Td className="font-medium text-white">{h.company}</Td>
                      <Td>
                        <p>{h.role_title}</p>
                        <p className="text-xs text-white/40">{h.location} · {h.hiring_type}</p>
                      </Td>
                      <Td>
                        <p className="text-xs">{h.contact_name}</p>
                        <p className="text-xs text-white/40">{h.email}</p>
                        {h.phone && <p className="text-xs text-white/40">{h.phone}</p>}
                      </Td>
                      <Td className="max-w-[260px]">
                        <p className="truncate text-xs text-white/50">{h.details || "—"}</p>
                        <p className="mt-1 text-xs text-white/30">
                          {[h.headcount && `HC: ${h.headcount}`, h.timeline, h.salary_range].filter(Boolean).join(" · ")}
                        </p>
                      </Td>
                      <Td className="text-xs text-white/40">{fmtDate(h.created_at)}</Td>
                      <Td>
                        <StatusSelect value={h.status} options={HIRE_STATUSES} testid={`admin-hire-status-${h.id}`} onSave={(s) => updateStatus("hire-requests", h.id, s, setHireRequests)} />
                      </Td>
                    </tr>
                  ))}
                  {hireRequests.length === 0 && (
                    <tr><Td className="py-10 text-center text-white/35" colSpan={7}>No hire requests yet.</Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "candidates" && (
            <div className="overflow-x-auto border border-white/[0.08]" data-testid="admin-candidates-table">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-surface">
                  <tr>
                    <Th>Reference</Th><Th>Name</Th><Th>Profile</Th><Th>Contact</Th><Th>Resume</Th><Th>Submitted</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.id} className="transition-colors hover:bg-panel/[0.02]">
                      <Td className="font-mono text-xs text-white/40">{c.reference}</Td>
                      <Td className="font-medium text-white">{c.name}</Td>
                      <Td>
                        <p className="text-xs">{c.current_title || "—"}</p>
                        <p className="text-xs text-white/40">{[c.sector, c.experience_years && `${c.experience_years} yrs`].filter(Boolean).join(" · ")}</p>
                      </Td>
                      <Td>
                        <p className="text-xs">{c.email}</p>
                        {c.phone && <p className="text-xs text-white/40">{c.phone}</p>}
                      </Td>
                      <Td>
                        {c.resume_file ? (
                          <button onClick={() => downloadResume(c.resume_file, c.resume_original)} className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:underline" data-testid={`admin-candidate-resume-${c.id}`}>
                            <Download size={13} /> {c.resume_original || "Download"}
                          </button>
                        ) : (
                          <span className="text-xs text-white/30">—</span>
                        )}
                      </Td>
                      <Td className="text-xs text-white/40">{fmtDate(c.created_at)}</Td>
                      <Td>
                        <StatusSelect value={c.status} options={CANDIDATE_STATUSES} testid={`admin-candidate-status-${c.id}`} onSave={(s) => updateStatus("candidates", c.id, s, setCandidates)} />
                      </Td>
                    </tr>
                  ))}
                  {candidates.length === 0 && (
                    <tr><Td className="py-10 text-center text-white/35" colSpan={7}>No candidate profiles yet.</Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "contacts" && (
            <div className="overflow-x-auto border border-white/[0.08]" data-testid="admin-contacts-table">
              <table className="w-full min-w-[900px]">
                <thead className="bg-surface">
                  <tr>
                    <Th>Reference</Th><Th>Name</Th><Th>Subject</Th><Th>Message</Th><Th>Submitted</Th><Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} className="transition-colors hover:bg-panel/[0.02]">
                      <Td className="font-mono text-xs text-white/40">{c.reference}</Td>
                      <Td>
                        <p className="font-medium text-white">{c.name}</p>
                        <p className="text-xs text-white/40">{c.email}</p>
                        {c.company && <p className="text-xs text-white/40">{c.company}</p>}
                      </Td>
                      <Td>{c.subject}</Td>
                      <Td className="max-w-[320px]"><p className="line-clamp-2 text-xs text-white/50">{c.message}</p></Td>
                      <Td className="text-xs text-white/40">{fmtDate(c.created_at)}</Td>
                      <Td>
                        <StatusSelect value={c.status} options={CONTACT_STATUSES} testid={`admin-contact-status-${c.id}`} onSave={(s) => updateStatus("contacts", c.id, s, setContacts)} />
                      </Td>
                    </tr>
                  ))}
                  {contacts.length === 0 && (
                    <tr><Td className="py-10 text-center text-white/35" colSpan={6}>No enquiries yet.</Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "settings" && (
            <form onSubmit={saveHero} className="max-w-2xl border border-white/[0.08] bg-surface p-8" data-testid="admin-settings-form">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-500">Site Settings</p>
              <p className="mt-3 text-sm leading-relaxed text-white/50">
                Replace the cinematic hero without redesigning the page. Paste a compressed MP4 URL
                (H.264, ideally under 6MB) and a high-quality poster image shown while the video
                loads and on slow connections. Leave the video URL empty to use the poster with a
                subtle cinematic motion effect.
              </p>
              <div className="mt-7 space-y-5">
                <div>
                  <label className="nx-label" htmlFor="hero-video">Hero Video URL (MP4)</label>
                  <input id="hero-video" className="nx-input" value={hero.video_url} onChange={(e) => setHero((h) => ({ ...h, video_url: e.target.value }))} data-testid="admin-hero-video-input" />
                </div>
                <div>
                  <label className="nx-label" htmlFor="hero-poster">Poster Image URL</label>
                  <input id="hero-poster" className="nx-input" value={hero.poster_url} onChange={(e) => setHero((h) => ({ ...h, poster_url: e.target.value }))} data-testid="admin-hero-poster-input" />
                </div>
                <div>
                  <label className="nx-label" htmlFor="notify-email">Internal Notification Email</label>
                  <input id="notify-email" type="email" className="nx-input" value={hero.notify_email} onChange={(e) => setHero((h) => ({ ...h, notify_email: e.target.value }))} data-testid="admin-notify-email-input" />
                  <p className="mt-2 text-xs text-white/35">Every form submission is emailed to this inbox. Change it anytime without a redeploy.</p>
                </div>
                <button type="submit" className="nx-btn-primary !px-6 !py-3" data-testid="admin-hero-save-btn">
                  Save Settings
                </button>
              </div>
            </form>
          )}

          {tab === "settings" && (
            <form onSubmit={changePassword} className="mt-8 max-w-2xl border border-white/[0.08] bg-surface p-8" data-testid="admin-password-form">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-500">Admin Account</p>
              <p className="mt-3 text-sm leading-relaxed text-white/50">
                Signed in as {user.email}. Change your password below — minimum 12 characters with
                upper case, lower case and a number. Your current session stays active.
              </p>
              <div className="mt-6 space-y-5">
                <div>
                  <label className="nx-label" htmlFor="pw-current">Current Password</label>
                  <input id="pw-current" type="password" className="nx-input" required value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} data-testid="admin-pw-current" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="nx-label" htmlFor="pw-next">New Password</label>
                    <input id="pw-next" type="password" className="nx-input" required minLength={12} value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} data-testid="admin-pw-next" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="pw-confirm">Confirm New Password</label>
                    <input id="pw-confirm" type="password" className="nx-input" required minLength={12} value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} data-testid="admin-pw-confirm" />
                  </div>
                </div>
                <button type="submit" className="nx-btn-primary !px-6 !py-3" data-testid="admin-pw-save-btn">
                  Change Password
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      {modal && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm lg:p-10" data-testid="admin-job-modal">
          <form onSubmit={saveJob} className="w-full max-w-3xl border border-white/10 bg-surface p-8 lg:p-10">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-light">{modal === "new" ? "New Role" : "Edit Role"}</h2>
              <button type="button" onClick={() => setModal(null)} aria-label="Close" data-testid="admin-job-modal-close">
                <X size={22} className="text-white/50 hover:text-white" />
              </button>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label className="nx-label">Title *</label>
                <input className="nx-input" required value={jobForm.title} onChange={(e) => setJobForm((f) => ({ ...f, title: e.target.value }))} data-testid="job-form-title" />
              </div>
              <div>
                <label className="nx-label">Location *</label>
                <input className="nx-input" required placeholder="e.g. Dubai, UAE" value={jobForm.location} onChange={(e) => setJobForm((f) => ({ ...f, location: e.target.value }))} data-testid="job-form-location" />
              </div>
              <div>
                <label className="nx-label">Country *</label>
                <input className="nx-input" required value={jobForm.country} onChange={(e) => setJobForm((f) => ({ ...f, country: e.target.value }))} data-testid="job-form-country" />
              </div>
              <div>
                <label className="nx-label">Sector *</label>
                <input className="nx-input" required value={jobForm.sector} onChange={(e) => setJobForm((f) => ({ ...f, sector: e.target.value }))} data-testid="job-form-sector" />
              </div>
              <div>
                <label className="nx-label">Type *</label>
                <select className="nx-input cursor-pointer appearance-none [&>option]:bg-obsidian" value={jobForm.type} onChange={(e) => setJobForm((f) => ({ ...f, type: e.target.value }))} data-testid="job-form-type">
                  {["Direct Hire", "Executive Search", "Contract", "RPO"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="nx-label">Seniority</label>
                <input className="nx-input" placeholder="e.g. Executive" value={jobForm.seniority} onChange={(e) => setJobForm((f) => ({ ...f, seniority: e.target.value }))} data-testid="job-form-seniority" />
              </div>
              <div>
                <label className="nx-label">Salary</label>
                <input className="nx-input" placeholder="e.g. AED 60,000 – 80,000 / month" value={jobForm.salary} onChange={(e) => setJobForm((f) => ({ ...f, salary: e.target.value }))} data-testid="job-form-salary" />
              </div>
              <div>
                <label className="nx-label">Status</label>
                <select className="nx-input cursor-pointer appearance-none [&>option]:bg-obsidian" value={jobForm.status} onChange={(e) => setJobForm((f) => ({ ...f, status: e.target.value }))} data-testid="job-form-status">
                  {["Open", "On Hold", "Closed", "Archived", "Draft"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="nx-label">Summary</label>
                <input className="nx-input" value={jobForm.summary} onChange={(e) => setJobForm((f) => ({ ...f, summary: e.target.value }))} data-testid="job-form-summary" />
              </div>
              <div className="sm:col-span-2">
                <label className="nx-label">Description</label>
                <textarea rows={4} className="nx-input" value={jobForm.description} onChange={(e) => setJobForm((f) => ({ ...f, description: e.target.value }))} data-testid="job-form-description" />
              </div>
              <div className="sm:col-span-2">
                <label className="nx-label">Responsibilities (one per line)</label>
                <textarea rows={4} className="nx-input" value={jobForm.responsibilities} onChange={(e) => setJobForm((f) => ({ ...f, responsibilities: e.target.value }))} data-testid="job-form-responsibilities" />
              </div>
              <div className="sm:col-span-2">
                <label className="nx-label">Requirements (one per line)</label>
                <textarea rows={4} className="nx-input" value={jobForm.requirements} onChange={(e) => setJobForm((f) => ({ ...f, requirements: e.target.value }))} data-testid="job-form-requirements" />
              </div>
              <label className="flex items-center gap-3 text-sm text-white/60 sm:col-span-2">
                <input type="checkbox" checked={jobForm.featured} onChange={(e) => setJobForm((f) => ({ ...f, featured: e.target.checked }))} className="h-4 w-4 accent-emerald-600" data-testid="job-form-featured" />
                Feature on homepage
              </label>
              <label className="flex items-center gap-3 text-sm text-white/60 sm:col-span-2">
                <input type="checkbox" checked={jobForm.demo} onChange={(e) => setJobForm((f) => ({ ...f, demo: e.target.checked }))} className="h-4 w-4 accent-emerald-600" data-testid="job-form-demo" />
                Mark as demonstration listing (publicly labelled DEMO)
              </label>
            </div>
            <div className="mt-8 flex gap-3">
              <button type="submit" className="nx-btn-primary !px-6 !py-3" data-testid="job-form-submit">
                {modal === "new" ? "Publish Role" : "Save Changes"}
              </button>
              <button type="button" onClick={() => setModal(null)} className="nx-btn-outline !px-6 !py-3" data-testid="job-form-cancel">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Admin;
